import { Box, Stack, Tooltip, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { operationsDashboardStyles } from "../styles";
import { colors } from "../../../utils/colors";
import { DashboardHeaderProps, Legend } from "../type";
import FilterIcon from "../../../assets/img/sliders-horizontal.svg";
import CostViewToggle from "./CostToggleView";
import { Role } from "../../../utils/roles";

const defaultLegends: Legend[] = [
  {
    color: colors.legend_red,
    label: [">10%", "below Budget"],
    mobileLabel: ">10% below Budget",
  },
  {
    color: colors.legend_orange,
    label: ["Upto 10%", "below Budget"],
    mobileLabel: "Upto 10% below Budget",
  },
  {
    color: colors.legend_green,
    label: ["In-line", "with Budget"],
    mobileLabel: "In-line with Budget",
  },
];

export const DashboardHeader = ({
  title = "Operations Dashboard",
  legends = defaultLegends,
  hasActiveFilters = false,
  onFilterClick,
  costType = "actual",
  onCostTypeChange,
  showLegends = true,
  showCostToggle = true,
  infoText,
}: DashboardHeaderProps & {
  costType?: string;
  onCostTypeChange?: (costType: string) => void;
}) => {
  const userInfo = JSON.parse(localStorage.getItem("userDetails") ?? "{}");
  const userRoles = userInfo.roles;
  const hasCeoRolesAccess = userInfo.has_ceo_roles_access;
  const isDirector = userRoles?.includes(Role.ROLE_DIRECTORS);
  const roleEligibleForCostToggle = hasCeoRolesAccess || isDirector;
  const showCostToggleUI = showCostToggle && roleEligibleForCostToggle;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const effectiveCostType = roleEligibleForCostToggle ? costType : "standard";

  useEffect(() => {
    if (onCostTypeChange && !roleEligibleForCostToggle) {
      onCostTypeChange("standard");
    }
  }, [roleEligibleForCostToggle, onCostTypeChange]);

  return (
    <Box>
      <Box sx={operationsDashboardStyles.header}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={operationsDashboardStyles.dashboardTitle}>
            {title}
          </Typography>
          {infoText ? (
            <Tooltip title={infoText} arrow placement="right">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  cursor: "pointer",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
              </Box>
            </Tooltip>
          ) : null}
        </Stack>

        {isMobile ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={operationsDashboardStyles.filterIconContainer}
              onClick={onFilterClick}
            >
              <img
                style={operationsDashboardStyles.filterIcon}
                src={FilterIcon}
                alt="filter icon"
              />
              {hasActiveFilters && (
                <Box sx={operationsDashboardStyles.filterIconBadge} />
              )}
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" sx={operationsDashboardStyles.legendContainer}>
            {showCostToggleUI && (
              <Stack
                direction="row"
                sx={operationsDashboardStyles.marginRight24}
              >
                <CostViewToggle
                  value={effectiveCostType}
                  onChange={onCostTypeChange ?? (() => {})}
                />
              </Stack>
            )}
            {showLegends &&
              legends.map((legend, index) => (
                <Stack
                  key={`legend-${legend.mobileLabel}-${legend.color}`}
                  direction="row"
                  sx={operationsDashboardStyles.legendContainer}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={operationsDashboardStyles.legendItemContainer}
                  >
                    <Box
                      sx={{
                        ...operationsDashboardStyles.legendIndicator,
                        backgroundColor: legend.color,
                      }}
                    />
                    <Box>
                      {legend.label.map((text) => (
                        <Typography
                          key={`${legend.mobileLabel}-${text}`}
                          sx={{
                            ...operationsDashboardStyles.legendText,
                            ...operationsDashboardStyles.legendTextDesktop,
                          }}
                        >
                          {text}
                        </Typography>
                      ))}
                    </Box>
                  </Stack>

                  {index < legends.length - 1 && (
                    <Box sx={operationsDashboardStyles.legendDivider} />
                  )}
                </Stack>
              ))}
          </Stack>
        )}
      </Box>

      {isMobile && (
        <Stack direction="column">
          {showCostToggleUI && (
            <Stack direction="row" spacing={2} sx={{ mb: "8px" }}>
              <CostViewToggle
                value={effectiveCostType}
                onChange={onCostTypeChange ?? (() => {})}
              />
            </Stack>
          )}
          {showLegends && (
            <Stack
              direction="row"
              sx={{ ...operationsDashboardStyles.legendContainer }}
            >
              {legends.map((legend, index) => (
                <Stack
                  key={`mobile-legend-${legend.mobileLabel}-${legend.color}`}
                  direction="row"
                  sx={operationsDashboardStyles.legendContainerMobile}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={operationsDashboardStyles.legendItemContainer}
                  >
                    <Box
                      sx={{
                        ...operationsDashboardStyles.legendIndicator,
                        backgroundColor: legend.color,
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          ...operationsDashboardStyles.legendText,
                          ...operationsDashboardStyles.legendTextMobile,
                        }}
                      >
                        {legend.mobileLabel}
                      </Typography>
                    </Box>
                  </Stack>

                  {index < legends.length - 1 && (
                    <Box sx={operationsDashboardStyles.legendDivider} />
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};
